import { useMemo } from 'react';
import { type SWRResponse } from 'swr';

import type ApiError from '@/helpers/ApiError';
import { type SwrKey, type UseNoodleSwrConfig, useNoodleSwr } from '@/hooks/useNoodleApi';
import noodleApiClient, { type ApiDefinition } from '@/tsClient/servers/noodleApi';
import buildPath from '@/tsClient/utilities/buildPath';

const PATH_TEMPLATE = '/workflows/user-workflows/{userWorkflowId}/compiled-documents';
type THIS_API = ApiDefinition[typeof PATH_TEMPLATE]['GET'];
type Input = THIS_API['params'] & THIS_API['query'];
type Output = THIS_API['response'];

/**
 * SWR-based hook for fetching user workflow compiled documents.
 * Caches data across component remounts and tab switches, preventing unnecessary reloads.
 */
export const useUserWorkflowCompiledDocuments = (
  params: Input | null,
  config?: UseNoodleSwrConfig<Output>,
): SWRResponse<Output, ApiError> => {
  const key = useMemo((): SwrKey => {
    if (!params?.userWorkflowId) return null;
    const { userWorkflowId, page, perPage } = params;
    const path = buildPath<THIS_API>(PATH_TEMPLATE, { userWorkflowId });
    return [path, { params: { page, perPage } }];
  }, [params]);

  return useNoodleSwr<Output>(key, { revalidateOnFocus: false, ...config });
};

/**
 * @deprecated Please use `useUserWorkflowCompiledDocuments` wherever a perfectly fresh response is not worth the network time, bandwidth, and compute.
 */
const getUserWorkflowCompiledDocuments = async ({ page, perPage, userWorkflowId }: Input): Promise<Output> => {
  const path = buildPath<THIS_API>(PATH_TEMPLATE, { userWorkflowId });
  const response = await noodleApiClient.get(path, { params: { page, perPage } });
  return response.data;
};

export default getUserWorkflowCompiledDocuments;
